/* eslint-disable no-unused-vars */
/* eslint-disable import/prefer-default-export */
import gql from 'graphql-tag';

import { getGraphqlClient } from './client';
import { ERROR_MESSAGE_UNEXPECTED_OWNER_RESPONSE } from '../../consts';
import { log } from '../../log';

const mutateResponseDataForState = (context, response) => {
  const { data } = response;
  if (!data) return log.error('[🪐 serverStateFromApi] response %o', response);
  const serverState = {
    // if we get an owners obj back and the username is root we don't want to overwrite the values
    ...(data.owner && data.owner.username !== 'root' && {
      avatar: data.owner.avatar,
      username: data.owner.username,
      registered: true,
    }),
    // if owners obj is empty we need to sign user out
    ...(context.state.signOutTriggered && { username: '', avatar: '', registered: false }),
    servername: (data.info && data.info.os) ? data.info.os.hostname : null,
    guid: data.flash ? data.flash.guid : null,
    keyfile: (data.registration && data.registration.keyFile) ? data.registration.keyFile.contents : null,
    flashProduct: data.flash ? data.flash.product : null,
    flashvendor: data.flash ? data.flash.vendor : null,
    sendCrashInfo: data.crashReportingEnabled,
    reggen: data.vars ? data.vars.regGen : null,
    state: data.vars ? data.vars.regState : null,
    config: data.config ? data.config : {
      error: data.vars ? data.vars.configError : null,
      valid: data.vars ? data.vars.configValid : true,
    },
    expiretime: (data.registration && data.registration.expiration) ? data.registration.expiration : 0,
    ...(data.cloud && { cloud: data.cloud }),
  };
  log.debug('[🪐 serverStateFromApi] mutated response %o', serverState);
  return serverState;
};

/**
 * - if we haven't triggered a signout and we have an empty owners object this is an error
 * -  or username of "root" is an error
*/
const detectOwnersResponseError = (context, response) => {
  log.debug('[detectOwnersResponseError]', {
    'context.state.registered': context.state.registered,
    'context.state.signOutTriggered': context.state.signOutTriggered,
    'response.data.owner': response.data.owner,
    'context.state.myServersError': context.state.myServersError,
  });
  if (context.state.signOutTriggered) return log.debug('[detectOwnersResponseError] signed out; ignoring');
  if (!response.data.owner || (response.data.owner && response.data.owner.username === 'root')) {
    log.error(ERROR_MESSAGE_UNEXPECTED_OWNER_RESPONSE, response.data.owner);
    context.commit('SET_MY_SERVERS_ERROR', `${ERROR_MESSAGE_UNEXPECTED_OWNER_RESPONSE} Please update your Connect plugin (formly known as My Servers) to <em>potentially</em> fix this error. Thanks for working with us as we try to solve this bug.`);
    return context.commit('SET_MY_SERVERS_ERROR_SHOW_SUPPORT_BTN', true);
  }
  if (response.data.owner && response.data.owner.username !== 'root' && context.state.myServersError && context.state.myServersError.includes(ERROR_MESSAGE_UNEXPECTED_OWNER_RESPONSE)) {
    context.commit('SET_MY_SERVERS_ERROR', '');
    return context.commit('SET_MY_SERVERS_ERROR_SHOW_SUPPORT_BTN', false);
  }
  return log.debug('[detectOwnersResponseError] PASSED');
};

export const serverStateFromApi = async (context) => {
  log.debug('[serverStateFromApi] %o', { apiVersion: context.state.apiVersion });
  const query = gql`query serverState {
    owner {
      username
      avatar
    }
    info {
      os {
        hostname
      }
    }
    flash {
      guid
      vendor
      product
    }
    registration {
      expiration
      keyFile {
        contents
      }
    }
    crashReportingEnabled
    vars {
      regGen
      regState
      configError
      configValid
    }
    config {
      error
      valid
    }
    cloud {
      error
      apiKey {
        valid
        error
      }
      relay {
        status
        error
      }
      cloud {
        status
        error
      }
    }
  }`;
  /** to grab the inital server state */
  const response = await getGraphqlClient(context).query({
    errorPolicy: 'all',
    query,
  });
  log.debug('[🪐 serverStateFromApi] response %o', response, context);
  // if we have a cloud error, allow the user to contact support about it
  context.commit('SET_MY_SERVERS_ERROR_SHOW_SUPPORT_BTN', true);
  context.commit('SET_MY_SERVERS_LOADING', false);
  // potentially set hasRemoteApiKey
  context.commit('SET_FULL_SERVER_STATE', mutateResponseDataForState(context, response));
  detectOwnersResponseError(context, response); // is owners response acting weird?

  log.debug('[🪐 serverStateFromApi] subscriptions %o', { skip: context.state.graphReconnected });
  if (!context.state.graphReconnected) { // prevent duplicate subscriptions
    /**
     * subscribe to vars
     */
    log.debug('[🪐 serverStateFromApi] serverVarsSub');
    getGraphqlClient(context).subscribe({
      query: gql`subscription serverVarsSub {
        vars {
          regGen
          regState
          configError
          configValid
        }
      }`,
    }).subscribe({ /** `subscribe().subscribe()` I know, wtf right? */
      next(res) {
        log.debug('[🪐 serverStateFromApi] serverVarsSub res %o', res);
        context.commit('SET_STATE', res.data.vars.regState);
      },
      error(err) {
        context.commit('SET_MY_SERVERS_ERROR', err);
        return context.state.myServersError;
      },
    });

    /**
     * subscribe to owner
     */
    log.debug('[🪐 serverStateFromApi] serverOwnerSub');
    getGraphqlClient(context).subscribe({
      query: gql`subscription serverOwnerSub {
        owner {
          username
          avatar
        }
      }`,
    }).subscribe({ /** `subscribe().subscribe()` I know, wtf right? */
      next(res) {
        log.debug('[🪐 serverStateFromApi] serverOwnerSub res %o', res);
        detectOwnersResponseError(context, res); // is owners response acting weird?
        // only set when we have data and username is not "root"
        if (res && res.data && res.data.owner && res.data.owner.username !== 'root') context.commit('SET_OWNER', res.data.owner);
      },
      error(err) {
        context.commit('SET_MY_SERVERS_ERROR', err);
        return context.state.myServersError;
      },
    });

    /**
     * subscribe to config
     */
    log.debug('[🪐 serverStateFromApi] serverConfigSub');
    getGraphqlClient(context).subscribe({
      query: gql`subscription serverConfigSub {
        config {
          error
          valid
        }
      }`,
    }).subscribe({ /** `subscribe().subscribe()` I know, wtf right? */
      next(res) {
        log.debug('[🪐 serverStateFromApi] serverConfigSub res %o', res);
        context.commit('SET_CONFIG', res.data.config);
      },
      error(err) {
        context.commit('SET_MY_SERVERS_ERROR', err);
        return context.state.myServersError;
      },
    });
    /**
     * subscribe to owner
     */
    log.debug('[🪐 serverStateFromApi] serverRegistrationSub');
    getGraphqlClient(context).subscribe({
      query: gql`subscription serverRegistrationSub {
        registration {
          expiration
          keyFile {
            contents
          }
        }
      }`,
    }).subscribe({ /** `subscribe().subscribe()` I know, wtf right? */
      next(res) {
        log.debug('[🪐 serverStateFromApi] serverRegistrationSub res %o', res);
        context.commit('SET_REGISTRATION_SUB', res.data.registration);
      },
      error(err) {
        context.commit('SET_MY_SERVERS_ERROR', err);
        return context.state.myServersError;
      },
    });
  }

  log.debug('[🪐 serverStateFromApi] DONE %o', response.data);
  return response.data;
};

export const fetchServers = async (context) => {
  log.debug('[fetchServers]');
  context.commit('SET_MY_SERVERS_LOADING', true);
  /**
   * Initial query for servers data
   * @see: https://www.apollographql.com/docs/react/api/core/ApolloClient/#ApolloClient.query
   */
  const response = await getGraphqlClient(context).query({
    query: gql`query initialGetServers {
      servers {
        name
        guid
        status
      }
    }`,
  });
  log.debug('[fetchServers] response %o', response);
  const { data } = response;
  if (data) {
    /**
     * get wan ip to assist with determining local or remote URL for server dropdown.
     * We only need this when 2 or more servers b/c anything more than 1 means other servers.
     */
    if (data.servers.length > 1) context.dispatch('getWanIp');
    /** commit response data to vuex store */
    context.commit('SET_GRAPH_ONLINE', true);
    context.commit('SET_MY_SERVERS', data.servers);
    context.commit('SET_MY_SERVERS_LOADING', false);
  }
  log.debug('[fetchServers] getServers subscription %o', { skip: context.state.graphReconnected });
  if (!context.state.graphReconnected) { // prevent duplicate subscriptions
    /**
     * subscribe to servers
     */
    getGraphqlClient(context).subscribe({
      query: gql`subscription getServers {
        servers {
          name
          guid
          status
        }
      }`,
    }).subscribe({ /** `subscribe().subscribe()` I know, wtf right? */
      next(res) {
        log.debug('[new subscription data] %o', res);
        // if graph went offline, let's go back online
        if (!context.state.graphOnline) context.commit('SET_GRAPH_ONLINE', true);
        context.commit('SET_MY_SERVERS', res.data.servers);
        context.commit('SET_MY_SERVERS_LOADING', false);
      },
      error(err) {
        context.commit('SET_MY_SERVERS_ERROR', err);
        return context.state.myServersError;
      },
    });
  }
};
